import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledTooltip
} from 'reactstrap';
import SimpleHeader from 'components/Headers/SimpleHeader.js';
import { Link } from 'react-router-dom';
import { GET_SURVEYS } from 'graphql/queries';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { SURVEY_STATUS_UPDATE, SURVEY_DUPLICATE } from 'graphql/mutations';
import ToggleSurvey from './ToggleSurvey';
import useConfirmation from 'hooks/useConfirmation';
import useNotificationAlert from 'hooks/useNotificationAlert';
import Pagination from 'components/Pagination';
import usePagination from 'hooks/usePagination';
import { graphqlStatusToStatusMap } from 'constants/global';
import ModalShareSurvey from './ModalShareSurvey';
import { getSurveyUrl } from 'helpers/global';
import { viewSurveyTitle } from 'helpers/global';
import { SURVEY_FULL_PATH } from 'constants/paths';
import { ARCHIVED_SURVEY_FULL_PATH } from 'constants/paths';
import { ADD_SURVEY_FULL_PATH } from 'constants/paths';
import moment from 'moment';
import { SURVEY_COPY_AS_A_TEMPLATE } from 'graphql/mutations';
import { useSelector } from 'react-redux';
import ModalProPlanRestriction from 'components/modals/ModalProPlanRestriction';
import { SELECT_TEMPLATE_FULL_PATH } from 'constants/paths';
import { useSortBy, useTable } from 'react-table';

const sortingKeys = {
  name: 'NAME',
  updatedAt: 'UPDATED_AT',
  createdByName: 'CREATED_BY',
  updatedByName: 'UPDATED_BY',
  lastResponse: 'LAST_RESPONSE',
  totalResponses: 'RESPONSES_COUNT',
  status: 'STATUS',
  active: 'ACTIVE'
};

const perPage = 10;
const TABLE_STATE_STORAGE_KEY = 'survey_table_state';

const getInitialTableState = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const rawState = window.localStorage.getItem(TABLE_STATE_STORAGE_KEY);

    if (!rawState) {
      return {};
    }

    const parsedState = JSON.parse(rawState);
    const sortBy = Array.isArray(parsedState?.sortBy) ? parsedState.sortBy : [];
    const validSortBy = sortBy
      .filter((item) => item?.id && Object.prototype.hasOwnProperty.call(sortingKeys, item.id))
      .map((item) => ({ id: item.id, desc: Boolean(item.desc) }));

    return { sortBy: validSortBy };
  } catch (err) {
    return {};
  }
};

const getOrderByFromSortBy = (sortBy = []) => {
  const firstSort = sortBy[0];

  if (!firstSort) {
    return undefined;
  }

  return [
    {
      column: sortingKeys[firstSort.id],
      order: firstSort.desc ? 'DESC' : 'ASC'
    }
  ];
};

const isSameSortBy = (left = [], right = []) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every(
    (item, index) => item?.id === right[index]?.id && Boolean(item?.desc) === Boolean(right[index]?.desc)
  );
};

const Survey = (props) => {
  const { pageNumber, goToPage } = usePagination({ props });
  const { me, currentPlan } = useSelector((state) => state.auth);
  const initialTableState = useMemo(() => getInitialTableState(), []);
  const [activeSortBy, setActiveSortBy] = useState(initialTableState.sortBy || []);

  const {
    loading: isLoadingDatas,
    data,
    refetch,
    networkStatus
  } = useQuery(GET_SURVEYS, {
    variables: {
      statuses: ['PUBLISHED', 'DRAFT'],
      first: perPage,
      page: pageNumber,
      orderBy: getOrderByFromSortBy(activeSortBy)
    },
    notifyOnNetworkStatusChange: true
  });

  const [surveyStatusUpdate, { loading: isLoadingSurveyStatusUpdate }] =
    useMutation(SURVEY_STATUS_UPDATE);

  const [surveyDuplicate, { loading: isLoadingSurveyDuplicate }] = useMutation(SURVEY_DUPLICATE);
  const [copySurveyAsATemplate, { loading: isLoadingSurveyCopyAsATemplate }] =
    useMutation(SURVEY_COPY_AS_A_TEMPLATE);

  const { showConfirmation, hideConfirmation } = useConfirmation(
    isLoadingSurveyDuplicate || isLoadingSurveyStatusUpdate || isLoadingSurveyCopyAsATemplate
  );

  const { show: showNotificationAlert } = useNotificationAlert({
    place: 'br'
  });

  const latestSortByRef = useRef(initialTableState.sortBy || []);

  const refetchSurveys = useCallback(() => {
    return refetch({
      page: pageNumber,
      orderBy: getOrderByFromSortBy(latestSortByRef.current)
    });
  }, [pageNumber, refetch]);

  const onArchive = useCallback(
    async (id) => {
      try {
        await surveyStatusUpdate({ variables: { id, status: 'ARCHIVED' } });
        showNotificationAlert({
          title: 'Success',
          message: 'You have archived the survey successfully!'
        });
        await refetchSurveys();
      } catch (err) {
        switch (err.message) {
          default:
            showNotificationAlert({
              type: 'danger',
              title: 'Failed',
              message: err.message
            });
            break;
        }
      } finally {
        hideConfirmation();
      }
    },
    [hideConfirmation, refetchSurveys, showNotificationAlert, surveyStatusUpdate]
  );

  const onDuplicate = useCallback(
    async (surveyId) => {
      try {
        await surveyDuplicate({ variables: { surveyId } });
        showNotificationAlert({
          title: 'Success',
          message: 'You have duplicated the survey successfully!'
        });
        await refetchSurveys();
      } catch (err) {
        switch (err.message) {
          default:
            showNotificationAlert({
              type: 'danger',
              title: 'Failed',
              message: err.message
            });
            break;
        }
      } finally {
        hideConfirmation();
      }
    },
    [hideConfirmation, refetchSurveys, showNotificationAlert, surveyDuplicate]
  );

  const onCopyAsATemplate = useCallback(
    async (data) => {
      const surveyId = data.id;

      if (currentPlan !== 'PRO') {
        setShowModalTemplateRestriction(true);
        return;
      }

      showConfirmation({
        title: `Are you sure you want to create a template from "${data.name}"?`,
        confirmTitle: 'Create Template',
        onConfirm: async () => {
          try {
            await copySurveyAsATemplate({ variables: { surveyId } });
            showNotificationAlert({
              title: 'Success',
              message: 'You have created a template successfully!'
            });
            await refetchSurveys();
          } catch (err) {
            switch (err.message) {
              case 'preset_data_cannot_be_included':
                showNotificationAlert({
                  type: 'danger',
                  title: 'Failed',
                  message:
                    'You cannot create a template from a survey containing a "Preset Data" question.'
                });
                break;
              default:
                showNotificationAlert({
                  type: 'danger',
                  title: 'Failed',
                  message: err.message
                });
                break;
            }
          } finally {
            hideConfirmation();
          }
        }
      });
    },
    [
      copySurveyAsATemplate,
      currentPlan,
      hideConfirmation,
      refetchSurveys,
      showConfirmation,
      showNotificationAlert
    ]
  );

  const columns = useMemo(
    () => [
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Name{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'name',
        style: { whiteSpace: 'normal' }
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Author{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'createdByName'
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Last Edited{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'updatedAt',
        Cell: ({ value }) => {
          return <span className="text-muted">{moment(value).format('DD/MM/YYYY')}</span>;
        }
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Last Edited By{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'updatedByName'
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Last Response{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'lastResponse',
        Cell: ({ value }) => {
          return value ? (
            <span className="text-muted">{moment(value).format('DD/MM/YYYY')}</span>
          ) : (
            ''
          );
        }
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Responses{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        accessor: 'totalResponses'
      },
      {
        Header: ({ column }) => (
          <span className="heading-w-icon">
            Status{' '}
            {column.isSorted ? (
              column.isSortedDesc ? (
                <span className="sorted-icon sorted-icon__desc" />
              ) : (
                <span className="sorted-icon sorted-icon__asc" />
              )
            ) : (
              <span className="sorted-icon" />
            )}
          </span>
        ),
        Cell: ({ cell }) => {
          return (
            <Badge color={cell.row.original.status === 'PUBLISHED' ? 'success' : 'danger'}>
              {graphqlStatusToStatusMap[cell.row.original.status]}
            </Badge>
          );
        },
        accessor: 'status'
      },
      {
        Header: () => 'Active',
        Cell: ({ cell }) => {
          return (
            <>
              {cell.row.original.status === 'PUBLISHED' && (
                <ToggleSurvey
                  id={cell.row.original.id}
                  active={cell.row.original.active}
                  onChange={(value) => onChangeToggle(cell.row.index, value)}
                />
              )}
            </>
          );
        },
        id: 'active'
      },
      {
        Header: () => 'Manage',
        Cell: ({ cell }) => {
          return (
            <div className="d-flex">
              {cell.row.original.totalQuestions >= 1 && me?.visibility === 'FULL' && (
                <>
                  <button
                    className="table-action table-action-preview"
                    id={`tooltip-preview-${cell.row.original.id}`}
                    onClick={(e) =>
                      onPreviewSurvey(cell.row.original.id, cell.row.original.status)
                    }>
                    <i className="fas fa-eye" />
                  </button>
                  <UncontrolledTooltip delay={0} target={`tooltip-preview-${cell.row.original.id}`}>
                    {viewSurveyTitle(cell.row.original.status === 'PUBLISHED')}
                  </UncontrolledTooltip>
                </>
              )}
              <Link
                to={`${SURVEY_FULL_PATH}/${cell.row.original.id}/result`}
                className="table-action table-action-result"
                id={`tooltip-result-${cell.row.original.id}`}>
                <i className="fas fa-list" />
              </Link>
              <UncontrolledTooltip delay={0} target={`tooltip-result-${cell.row.original.id}`}>
                View Results
              </UncontrolledTooltip>
              {me?.visibility === 'FULL' && (
                <>
                  <Link
                    className="table-action"
                    to={`${SURVEY_FULL_PATH}/${cell.row.original.id}/edit`}
                    id={`tooltip-edit-${cell.row.original.id}`}>
                    <i className="fas fa-user-edit" />
                  </Link>
                  <UncontrolledTooltip delay={0} target={`tooltip-edit-${cell.row.original.id}`}>
                    Edit
                  </UncontrolledTooltip>

                  <button
                    className="table-action table-action-duplicate"
                    id={`tooltip-duplicate-${cell.row.original.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      showConfirmation({
                        title: `Are you sure want to duplicate "${cell.row.original.name}"?`,
                        onConfirm: () => onDuplicate(cell.row.original.id),
                        confirmTitle: 'Duplicate'
                      });
                    }}>
                    <i className="ni ni-ungroup" />
                  </button>
                  <UncontrolledTooltip
                    delay={0}
                    target={`tooltip-duplicate-${cell.row.original.id}`}>
                    Duplicate
                  </UncontrolledTooltip>

                  <button
                    className="table-action table-action-duplicate"
                    id={`tooltip-copy-template-${cell.row.original.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      onCopyAsATemplate(cell.row.original);
                    }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width={16}>
                      <path
                        fill="#adb5bd"
                        d="M192 64L64 64l0 128 128 0 0-128zM64 32l128 0 32 0 0 32 0 128 0 32-32 0L64 224l-32 0 0-32L32 64l0-32 32 0zM192 320L64 320l0 128 128 0 0-128zM64 288l128 0 32 0 0 32 0 128 0 32-32 0L64 480l-32 0 0-32 0-128 0-32 32 0zM320 64l0 128 128 0 0-128L320 64zM288 32l32 0 128 0 32 0 0 32 0 128 0 32-32 0-128 0-32 0 0-32 0-128 0-32zM400 272l0 16 0 80 80 0 16 0 0 32-16 0-80 0 0 80 0 16-32 0 0-16 0-80-80 0-16 0 0-32 16 0 80 0 0-80 0-16 32 0z"
                      />
                    </svg>
                  </button>
                  <UncontrolledTooltip
                    delay={0}
                    target={`tooltip-copy-template-${cell.row.original.id}`}>
                    Copy As Template
                  </UncontrolledTooltip>

                  <button
                    className="table-action table-action-archive"
                    id={`tooltip-archive-${cell.row.original.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      showConfirmation({
                        title: `Are you sure want to archive "${cell.row.original.name}"?`,
                        confirmTitle: 'Archive',
                        onConfirm: () => onArchive(cell.row.original.id)
                      });
                    }}>
                    <i className="ni ni-archive-2" />
                  </button>
                  <UncontrolledTooltip delay={0} target={`tooltip-archive-${cell.row.original.id}`}>
                    Archive
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          );
        },
        id: 'action'
      }
    ],
    [me, onArchive, onCopyAsATemplate, onDuplicate, showConfirmation]
  );

  const [surveys, setSurveys] = useState();

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, state: tableState } = useTable(
    {
      columns,
      data: surveys || [],
      manualSortBy: true,
      disableSortRemove: true,
      initialState: initialTableState
    },
    useSortBy
  );

  useEffect(() => {
    const nextSortBy = tableState.sortBy || [];

    latestSortByRef.current = nextSortBy;

    if (!isSameSortBy(activeSortBy, nextSortBy)) {
      setActiveSortBy(nextSortBy);
    }

    try {
      window.localStorage.setItem(
        TABLE_STATE_STORAGE_KEY,
        JSON.stringify({ sortBy: nextSortBy })
      );
    } catch (err) {
      // Ignore storage errors (e.g. disabled storage/private mode).
    }
  }, [activeSortBy, tableState.sortBy]);

  const [totalRow, setTotalRow] = useState(0);

  const [showModalTemplateRestriction, setShowModalTemplateRestriction] = useState(false);

  useEffect(() => {
    if (data && data.getSurveys) {
      setTotalRow(data.getSurveys.paginatorInfo.total);
      setSurveys(JSON.parse(JSON.stringify(data.getSurveys.data)));
    }
  }, [data]);

  const onChangeToggle = async (index, value) => {
    setSurveys((localSurveys) => {
      const newSurveys = [...localSurveys];
      newSurveys[index].active = value;
      return newSurveys;
    });
  };

  const [modalShareSurvey, setModalShareSurvey] = useState({
    url: '',
    isShow: false
  });

  const onPreviewSurvey = (surveyId, status) => {
    if (status !== 'PUBLISHED') {
      window.open(getSurveyUrl(surveyId, status));
    } else {
      setModalShareSurvey((localData) => ({
        ...localData,
        isShow: true,
        isPublished: status === 'PUBLISHED',
        url: getSurveyUrl(surveyId, status)
      }));
    }
  };

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setDropdownOpen((prevState) => !prevState);

  return (
    <>
      <ModalShareSurvey
        url={modalShareSurvey.url}
        onClose={() => setModalShareSurvey((localData) => ({ ...localData, isShow: false }))}
        isShow={modalShareSurvey.isShow}
        isPublished={modalShareSurvey.isPublished}
      />

      <ModalProPlanRestriction
        isShow={showModalTemplateRestriction}
        onClose={() => setShowModalTemplateRestriction(false)}
        level={me?.level}
      />

      <SimpleHeader
        name="My Area"
        description="
         Simply select from the icons on the right hand side, as to whether you want to; Share & Preview, View results, Edit, Duplicate, Create a template or Archive"
        firstColProps={{ xs: 12 }}
        secondColProps={{ xs: 12, className: 'mt-3 mt-sm-0' }}
        rightElements={
          me?.visibility === 'FULL' && (
            <>
              <Link
                to={ARCHIVED_SURVEY_FULL_PATH}
                className="btn btn-black btn-sm rounded-pill flex-grow-1 flex-sm-grow-0"
                color="default"
                size="sm">
                Archive
              </Link>

              <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                <DropdownToggle
                  caret
                  className="btn-neutral btn-sm rounded-pill flex-grow-1 flex-sm-grow-0 mr-0">
                  Create New
                </DropdownToggle>
                <DropdownMenu right className="mt-1">
                  <DropdownItem tag={Link} to={ADD_SURVEY_FULL_PATH}>
                    Create from Scratch
                  </DropdownItem>
                  <DropdownItem tag={Link} to={SELECT_TEMPLATE_FULL_PATH}>
                    Create from Template
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </>
          )
        }
      />
      <Container className="mt--6" fluid>
        <Row>
          <div className="col">
            <Card>
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h3 className="mb-0 text-brand">My Area</h3>
                  </Col>
                </Row>
              </CardHeader>
              {(isLoadingDatas || networkStatus === NetworkStatus.refetch) && (
                <CardBody>
                  <p className="text-center">Loading...</p>
                </CardBody>
              )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getSurveys?.data.length <= 0 && (
                  <CardBody>
                    <p className="text-center">There is no data!</p>
                  </CardBody>
                )}

              {!isLoadingDatas &&
                networkStatus !== NetworkStatus.refetch &&
                data?.getSurveys?.data.length >= 1 && (
                  <>
                    <Table
                      className="align-items-center table-flush"
                      responsive
                      striped
                      {...getTableProps()}>
                      <thead className="thead-light">
                        {headerGroups.map((headerGroup) => (
                          <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column) => (
                              <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                {column.tooltip ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: column.render('Header')
                                    }}></span>
                                ) : (
                                  column.render('Header')
                                )}
                              </th>
                            ))}
                          </tr>
                        ))}
                      </thead>

                      <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                          prepareRow(row);
                          return (
                            <tr {...row.getRowProps()}>
                              {row.cells.map((cell) => {
                                return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>;
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <CardFooter className="py-4">
                      <Pagination
                        totalRow={totalRow}
                        perPage={perPage}
                        initialPage={pageNumber - 1}
                        onPageChange={(data) => {
                          goToPage(data);
                        }}
                      />
                    </CardFooter>
                  </>
                )}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Survey;
